/* eslint-disable react/no-access-state-in-setstate */
import Axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import EmployerDatabaseResultTable from '../../components/Employer/EmployerDatabaseResultTable/EmployerDatabaseResultTable';
import SalaryTipModal from '../../components/Employer/SalaryTipModal/SalaryTipModal';
import Button from '../../components/Input/Button/Button';
import FormElementWithLabel from '../../components/Input/FormElementWithLabel/FormElementWithLabel';
import InputField from '../../components/Input/InputField/InputField';
/* COMPONENTS */
import SinglePageLayout from '../../components/Layout/SinglePageLayout/SinglePageLayout';
import PageNav from '../../components/Management/Layout/PageNav/PageNav';
import HeaderSection from '../../components/Resources/HeaderSection/HeaderSection';
import ResourceList from '../../components/Resources/ResourceList/ResourceList';
import SearchContainer from '../../components/Resources/SearchContainer/SearchContainer';
import { SnowplowPageViewTracker } from '../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { throttle } from '../../utilities/helperFunctions';
/* CONSTANTS */
import { companyFieldLabelProps } from '../MaternityLeaveResourceCenter/constants';
import {
    departmentFieldLabelProps,
    PAGE_LIMIT,
    SEARCH_API,
    TABLE_HEADERS,
    THROTTLE_INTERVAL,
    titleFieldLabelProps,
} from './constants';
import { PAGE_PROPS } from '../defaultProps';

/* STYLESHEET */
import './SalaryDatabase.scss';

export default class SalaryDatabase extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            showTipModal: false,
            title: '',
            department: '',
            companyName: '',
            sortBy: 'companyName',
            order: 'asc',
            limit: PAGE_LIMIT,
            page: 1,
            searchResults: [],
            tip: {
                companyName: null,
                companyId: null,
            },
        };

        this.throttlePushUrl = throttle(
            this.pushUrlToWindow,
            THROTTLE_INTERVAL
        );
    }

    componentDidMount = () => {
        const queryValues = this.getUrlParams();
        this.setState(queryValues, this.search);
    };

    getUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);

        const title = urlParams.get('title') || '';
        const department = urlParams.get('department') || '';
        const companyName = urlParams.get('company') || '';
        const page = parseInt(urlParams.get('page'), 10) || 1;
        const sortBy = urlParams.get('sortBy') || this.state.sortBy;
        const order = urlParams.get('order') || this.state.order;

        return { title, department, companyName, page, sortBy, order };
    };

    setUrlParams = () => {
        const {
            companyName,
            title,
            department,
            page,
            sortBy,
            order,
        } = this.state;

        if (window.history.pushState) {
            const { protocol, host, pathname } = window.location;

            const titleQuery = title ? `title=${title}&` : '';
            const departmentQuery = department
                ? `department=${department}&`
                : '';
            const companyQuery = companyName ? `company=${companyName}&` : '';
            const sortByQuery = sortBy ? `sortBy=${sortBy}&` : '';
            const orderQuery = order ? `order=${order}&` : '';
            const pageQuery = `page=${page}`;

            const url = `${protocol}//${host}${pathname}`;
            const queries = `${titleQuery}${departmentQuery}${companyQuery}${sortByQuery}${orderQuery}${pageQuery}`;

            const newUrl = `${url}?${queries}`;
            this.throttlePushUrl(newUrl);
        }
    };

    pushUrlToWindow = (newUrl) => {
        window.history.replaceState({}, '', newUrl);
    };

    flattenDataForTable = (data) => {
        const {
            bonus,
            companyId,
            companyName,
            department,
            salary,
            title,
        } = data;

        return [companyId, title, department, salary, bonus, companyName];
    };

    getCancelToken = () => {
        if (typeof this._source !== typeof undefined) {
            this._source.cancel('Operation canceled due to new request.');
        }

        // save the new request for cancellation
        this._source = Axios.CancelToken.source();

        return {
            cancelToken: this._source.token,
        };
    };

    search = async () => {
        const {
            title,
            department,
            companyName,
            sortBy,
            order,
            limit,
            page,
        } = this.state;

        this.setUrlParams();

        const cancelToken = this.getCancelToken();

        const payload = {
            filters: {
                title,
                department,
                companyName,
            },
            sorting: {
                type: sortBy,
                order,
            },
            companyPagination: {
                limit,
                page,
            },
        };

        try {
            const { data } = await Axios.post(SEARCH_API, payload, cancelToken);
            this.setState({
                searchResults: data.map(this.flattenDataForTable),
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    handleModalClose = () => this.setState({ showTipModal: false });

    handleChange = (stateKey) => (value) => {
        this.setState({ [stateKey]: value, page: 1 }, this.search);
    };

    handleFormReset = () => {
        this.setState(
            {
                title: '',
                department: '',
                companyName: '',
                page: 1,
            },
            this.search
        );
    };

    handleTipClick = (companyId, companyName) => {
        this.setState({
            showTipModal: true,
            tip: {
                companyId,
                companyName,
            },
        });
    };

    handleSort = (sortBy, order) =>
        this.setState({ sortBy, order }, this.search);

    handlePrevious = () =>
        this.setState({ page: this.state.page - 1 }, this.search);

    handleNext = () =>
        this.setState({ page: this.state.page + 1 }, this.search);

    renderSearchFields = () => {
        return (
            <SearchContainer onReset={this.handleFormReset}>
                <FormElementWithLabel fieldLabelProps={titleFieldLabelProps}>
                    <InputField
                        name="title"
                        placeholder="Enter a title"
                        inputValue={this.state.title}
                        onChange={this.handleChange('title')}
                    />
                </FormElementWithLabel>

                <FormElementWithLabel
                    fieldLabelProps={departmentFieldLabelProps}
                >
                    <InputField
                        name="department"
                        placeholder="Enter a department"
                        inputValue={this.state.department}
                        onChange={this.handleChange('department')}
                    />
                </FormElementWithLabel>

                <FormElementWithLabel fieldLabelProps={companyFieldLabelProps}>
                    <InputField
                        name="company"
                        placeholder="Enter a company"
                        inputValue={this.state.companyName}
                        onChange={this.handleChange('companyName')}
                    />
                </FormElementWithLabel>

                <Button
                    formAction="reset"
                    className="reset-button"
                    type="secondary-blue-gray"
                    value="Clear"
                />
            </SearchContainer>
        );
    };

    renderHeaderDescription = () => {
        return (
            <React.Fragment>
                The salary and bonus information here is based on anonymous
                member submissions and is constantly updated. If you find this
                information useful, <a href="/registration">join us</a> or{' '}
                <a href="/share-with-friends">share</a> to help other women in
                the workplace!
            </React.Fragment>
        );
    };

    renderSearchResultsTable = () => {
        return (
            <div className="data-table">
                <EmployerDatabaseResultTable
                    tableHeaders={TABLE_HEADERS}
                    rowData={this.state.searchResults.slice(0, 20)}
                    onTipClick={this.handleTipClick}
                    onSort={this.handleSort}
                    sortType={this.state.sortBy}
                    sortOrder={this.state.order}
                    companyNameIndex={5}
                />
            </div>
        );
    };

    renderPageNavigation = () => {
        const goPrev = this.state.page !== 1 ? this.handlePrevious : null;
        const goNext =
            this.state.searchResults.length > PAGE_LIMIT
                ? this.handleNext
                : null;
        const hasSearchResults = this.state.searchResults.length > 0;

        if (hasSearchResults) {
            return <PageNav goToPrevPage={goPrev} goToNextPage={goNext} />;
        }
    };

    render() {
        return (
            <div className="page-SalaryDatabase">
                <SnowplowPageViewTracker />
                <SinglePageLayout PAGE_PROPS={this.props.PAGE_PROPS}>
                    <SinglePageLayout.Header>
                        <HeaderSection
                            title="Salary and Bonus, by Title and Company"
                            description={this.renderHeaderDescription()}
                        />
                    </SinglePageLayout.Header>

                    <SinglePageLayout.Content>
                        <main>
                            {this.renderSearchFields()}
                            {this.renderSearchResultsTable()}
                            {this.renderPageNavigation()}
                        </main>

                        <aside>
                            <ResourceList
                                resources={this.props.relatedResources}
                            />
                        </aside>

                        {this.state.showTipModal && (
                            <SalaryTipModal
                                companyId={this.state.tip.companyId}
                                companyName={this.state.tip.companyName}
                                onClose={this.handleModalClose}
                                showModal={this.state.showTipModal}
                            />
                        )}
                    </SinglePageLayout.Content>
                </SinglePageLayout>
            </div>
        );
    }
}

SalaryDatabase.propTypes = {
    /**
     * array of resources to be rendered by ResourceList
     */
    PAGE_PROPS: PAGE_PROPS.isRequired,
    relatedResources: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            image: PropTypes.shape({
                src: PropTypes.string,
                alt: PropTypes.string,
            }),
            url: PropTypes.string,
        })
    ).isRequired,
};
